import { DayOfWeek, LocalDate, ZoneId } from "@js-joda/core";
import { omit } from "lodash";
import { ApiErrors } from "../consts/apiErrors.const";
import { AssertNever } from "../consts/assertNever.const";
import { DateUtils } from "../consts/dateUtils";
import { loadable, Loadable } from "../consts/loadable.const";
import {
  OfficeId,
  PatientId,
  PlanOfCareId,
  PlanOfCareItemId,
  PlanOfCareTypeId,
} from "../messages/ids";
import {
  PatientPlanOfCare,
  PatientPlansOfCareDuty,
  PlanOfCareDutyPeriod,
} from "../messages/plan_of_care";
import { PlanOfCareType } from "../messages/plan_of_care_type";
import { PlanOfCareService } from "../services/planOfCareService";
import { PlanOfCareTypeService } from "../services/planOfCareTypeService";
import { MfModalFactory } from "../services/mfModalFactory";
import { isDefined } from "../utils/generalUtils";
import { NEW_YORK_TZ } from "../../../../webapp-react/src/shared/utils/date-utils";

interface FormDuty {
  days: Set<DayOfWeek>;
  period: PlanOfCareDutyPeriod | null;
  frequency: { id: number | null };
  visitsPerWeek: { id: number | null };
  notes: string | null;
  /** Occurrences per scheduled day. Null (the default) means once. */
  timesPerDay: number | null;
  /**
   * Per-day overrides of `timesPerDay`, keyed by `DayOfWeek.name()`. Bound
   * directly by the number input under each selected day circle, so a day the
   * coordinator left blank is absent or null here and falls back to
   * `timesPerDay`. Days that are not selected are ignored entirely -- a day can
   * hold a stale value from having been selected and then unselected, and
   * `getTimesPerDayByDay` reads it through `days` rather than trusting it.
   */
  timesPerDayByDay: Record<string, number | null>;
  /** Only caregivers validated for the duty in their profile may perform it. */
  caregiverRestricted: boolean;
}

interface Form {
  startDate: {
    value: Date | null;
    isOpen: boolean;
  };
  endDate: {
    value: Date | null;
    isOpen: boolean;
  };
  duties: Map<PlanOfCareItemId, FormDuty>;
}

interface ValidFormDutyState {
  days: Set<DayOfWeek>;
  period: PlanOfCareDutyPeriod;
  frequency: number | null;
  visitsPerWeek: number | null;
  notes: string | null;
  timesPerDay: number | null;
  /**
   * Null whenever every selected day works out to the same count, so a plan that
   * does not use per-day counts is sent exactly as it was before this field
   * existed. Non-null only where the days genuinely differ.
   */
  timesPerDayByDay: Record<string, number> | null;
  caregiverRestricted: boolean;
}

interface ValidFormState {
  startDate: Date;
  endDate: Date | null;
  duties: Map<PlanOfCareItemId, ValidFormDutyState>;
}

export type InitialFormData = Partial<{
  startDate: LocalDate | null;
  endDate: LocalDate | null;
  selectedPlanOfCare: PatientPlanOfCare;
  duties: Map<PlanOfCareItemId, FormDuty>;
}>;

//! @ngInject
export class NewPlanOfCareModalCtrl implements ng.IController {
  static readonly $name = "newPlanOfCareModalCtrl";

  constructor(
    private $timeout: ng.ITimeoutService,
    private $uibModalInstance: ng.ui.bootstrap.IModalInstanceService,
    private mfModal: MfModalFactory,
    private toaster: toaster.IToasterService,
    private planOfCareService: PlanOfCareService,
    private planOfCareTypeService: PlanOfCareTypeService,
    private apiErrors: ApiErrors,
    private dateUtils: DateUtils,
    private assertNever: AssertNever,
    private initialFormData: InitialFormData | null,
    private patientId: PatientId,
    private patientOfficeId: OfficeId
  ) {}

  planOfCareType: Loadable<PlanOfCareType | null> = loadable.loading();
  form!: Form;
  overrideFromSelectedPlanOfCare: boolean = false;
  isSubmitting = false;

  // read from the template
  readonly altInputFormats = ["MM/dd/yyyy"];
  readonly frequencyOptions = [1, 2, 3, 4, 5, 6, 7].map((i) => ({ id: i, label: i }));

  $onInit() {
    this.setInitialForm(this.initialFormData);

    this.planOfCareTypeService
      .getByOfficeId({ officeId: this.patientOfficeId })
      .then((planOfCareType) => {
        this.planOfCareType = loadable.resolve(
          planOfCareType === null
            ? null
            : {
                ...planOfCareType,
                columns: planOfCareType.columns.filter((x) => x.type === "Task"),
              }
        );
      });
  }

  handleSubmit() {
    const validation = this.validateForm();

    // this condition should not happen in runtime, but keeping it for type safety
    if (!loadable.isResolved(this.planOfCareType) || this.planOfCareType.value === null) {
      console.warn("Plan of care type is unavailable");
      return;
    }

    // I'm not sure why planOfCareTypeId is nullable, but making sure the user will receive an input
    if (this.planOfCareType.value.planOfCareTypeId === null) {
      this.toaster.error("Plan of care type is unavailable");
      return;
    }

    if (validation.type === "Error") {
      this.toaster.error(validation.message);
      return;
    }

    const planOfCareTypeId = this.planOfCareType.value.planOfCareTypeId;

    const { state } = validation;

    this.isSubmitting = true;

    const selectedPlanOfCare = this.initialFormData?.selectedPlanOfCare;
    if (this.overrideFromSelectedPlanOfCare && isDefined(selectedPlanOfCare)) {
      const submittedDate = selectedPlanOfCare.submittedAt
        .atZone(ZoneId.of(NEW_YORK_TZ))
        .toLocalDateTime();
      const modal = this.mfModal.create({
        confirmLabel: "OK",
        variant: "danger",
        message: `This POC will have the signature of ${
          selectedPlanOfCare.signeeName
        } for date ${this.dateUtils.localDateTimeToMDYHMString(submittedDate)}`,
        subject: "Copy Signatures And Dates",
        onConfirm: () => {
          this.submitCreatePlanOfCare(planOfCareTypeId, state, selectedPlanOfCare.planOfCareId);
          modal.close();
        },
        onCancel: () => {
          this.isSubmitting = false;
          modal.close();
        },
      });
    } else {
      this.submitCreatePlanOfCare(planOfCareTypeId, state);
    }
  }

  submitCreatePlanOfCare(
    planOfCareTypeId: PlanOfCareTypeId,
    state: ValidFormState,
    overrideFromPlanOfCareId?: PlanOfCareId
  ) {
    return this.planOfCareService
      .create({
        startDate: this.dateUtils.dateToLocalDate(state.startDate),
        endDate: state.endDate !== null ? this.dateUtils.dateToLocalDate(state.endDate) : null,
        duties: Array.from(state.duties.entries()).map(([id, duty]) => ({
          planOfCareItemId: id,
          period: duty.period,
          daysAWeek: [...duty.days],
          frequency: duty.frequency,
          visitsPerWeek: duty.visitsPerWeek,
          notes: duty.notes,
          timesPerDay: duty.timesPerDay,
          timesPerDayByDay: duty.timesPerDayByDay,
          caregiverRestricted: duty.caregiverRestricted,
        })),
        patientId: this.patientId,
        planOfCareTypeId: planOfCareTypeId,
        overrideSignatureAndDateFromPlanOfCareId: overrideFromPlanOfCareId,
      })
      .then(() => {
        this.toaster.success("Plan of care created");
        this.$uibModalInstance.close("success");
      })
      .catch((error) => {
        this.$timeout(
          () => this.toaster.error(this.apiErrors.format(error, "Failed to create plan of care")),
          0
        );
      })
      .finally(() => {
        this.isSubmitting = false;
      });
  }

  handleCheckDay(params: { id: PlanOfCareItemId; day: DayOfWeek }) {
    const duty = this.form.duties.get(params.id) ?? null;

    // This shouldn't happen in the UI
    if (duty === null) {
      this.toaster.error("Can't check day on unselected duty");
      return;
    }
    const days = new Set(duty.days);
    days.has(params.day) ? days.delete(params.day) : days.add(params.day);

    this.updateSelectedDuty({
      id: params.id,
      period: "DAYS_A_WEEK",
      days: days,
    });
  }

  handleChangePeriod(params: { id: PlanOfCareItemId; period: PlanOfCareDutyPeriod }) {
    switch (params.period) {
      case "EVERY_VISIT":
      case "AS_REQUESTED":
      case "DAILY":
      case "WEEKLY":
      case "1_2X_WEEKLY":
      case "DAILY_AS_NEEDED":
      case "WEEKLY_AS_NEEDED":
      case "1_2X_WEEKLY_AS_NEEDED":
        return this.updateSelectedDuty({
          id: params.id,
          period: params.period,
          days: getInitialItem().days,
          frequency: getInitialItem().frequency,
          visitsPerWeek: getInitialItem().visitsPerWeek,
        });
      case "DAYS_A_WEEK":
        return this.updateSelectedDuty({
          id: params.id,
          period: params.period,
        });
      default:
        this.assertNever(params.period);
    }
  }

  handleCheckItem(params: { id: PlanOfCareItemId; checked: boolean }) {
    if (!params.checked) {
      this.form.duties.delete(params.id);
      return;
    }

    this.form.duties.set(params.id, getInitialItem());
  }

  private updateSelectedDuty(params: { id: PlanOfCareItemId } & Partial<FormDuty>) {
    const newChanges = omit(params, "id");
    const selectedDuty = this.form.duties.get(params.id) ?? null;

    this.form.duties.set(params.id, {
      ...getInitialItem(),
      ...selectedDuty,
      ...newChanges,
    });
  }

  private validateForm():
    | { type: "Error"; message: string }
    | { type: "Success"; state: ValidFormState } {
    if (!loadable.isResolved(this.planOfCareType)) {
      return { type: "Error", message: "Plan of care type is loading" };
    }

    if (this.form.startDate.value === null) {
      return {
        type: "Error",
        message: "Start date is required",
      };
    }

    if (this.form.duties.size === 0) {
      return {
        type: "Error",
        message: "At least one duty is required",
      };
    }

    const validDuties: Map<PlanOfCareItemId, ValidFormDutyState> = new Map();

    for (const [id, duty] of this.form.duties.entries()) {
      const item = (this.planOfCareType.value?.columns ?? []).find((i) => i.id === id) ?? null;

      if (item === null) {
        return {
          type: "Error",
          message: `Unknown item id: ${id}`,
        };
      }

      if (duty.period === null) {
        return {
          type: "Error",
          message: `Period is required for ${item.label}`,
        };
      }

      switch (duty.period) {
        case "AS_REQUESTED":
        case "EVERY_VISIT":
        case "DAILY":
        case "WEEKLY":
        case "1_2X_WEEKLY":
        case "DAILY_AS_NEEDED":
        case "WEEKLY_AS_NEEDED":
        case "1_2X_WEEKLY_AS_NEEDED":
          break;
        case "DAYS_A_WEEK":
          if (duty.frequency === null || duty.visitsPerWeek === null) {
            return {
              type: "Error",
              message: `Missing frequency data for ${item.label}`,
            };
          }
          break;
        default:
          this.assertNever(duty.period);
      }

      const timesPerDay = resolveTimesPerDay(duty, item.label);

      if (timesPerDay.type === "Error") {
        return timesPerDay;
      }

      validDuties.set(id, {
        period: duty.period,
        days: duty.days,
        frequency: "id" in duty.frequency ? duty.frequency.id : null,
        visitsPerWeek: "id" in duty.visitsPerWeek ? duty.visitsPerWeek.id : null,
        notes: duty.notes,
        timesPerDay: timesPerDay.timesPerDay,
        timesPerDayByDay: timesPerDay.timesPerDayByDay,
        caregiverRestricted: duty.caregiverRestricted === true,
      });
    }

    return {
      type: "Success",
      state: {
        duties: validDuties,
        startDate: this.form.startDate.value,
        endDate: this.form.endDate.value,
      },
    };
  }

  private setInitialForm(initialFormData: InitialFormData | null) {
    this.form = {
      startDate: {
        value:
          initialFormData?.startDate instanceof LocalDate
            ? this.dateUtils.localDateToDate(initialFormData.startDate)
            : null,
        isOpen: false,
      },
      endDate: {
        value:
          initialFormData?.endDate instanceof LocalDate
            ? this.dateUtils.localDateToDate(initialFormData.endDate)
            : null,
        isOpen: false,
      },
      duties: new Map<PlanOfCareItemId, FormDuty>(
        initialFormData !== null && initialFormData.duties instanceof Map
          ? [...initialFormData.duties.entries()].map(([id, duty]) => [
              id,
              {
                days: new Set(duty.days),
                period: duty.period,
                frequency: { id: duty.frequency.id ?? null },
                visitsPerWeek: { id: duty.visitsPerWeek.id ?? null },
                notes: duty.notes,
                timesPerDay: duty.timesPerDay ?? null,
                timesPerDayByDay: { ...(duty.timesPerDayByDay ?? {}) },
                caregiverRestricted: duty.caregiverRestricted === true,
              },
            ])
          : []
      ),
    };
  }
}

export function mapPlanOfCareItemIdsToFormDuties(
  planOfCareItemIds: PlanOfCareItemId[]
): Map<PlanOfCareItemId, FormDuty> {
  return planOfCareItemIds.reduce((acc, id) => {
    acc.set(id, getInitialItem());
    return acc;
  }, new Map<PlanOfCareItemId, FormDuty>());
}

export function mapPatientPlansOfCareDutiesToFormDuties(
  patientPlansOfCareDuties: PatientPlansOfCareDuty[]
): Map<PlanOfCareItemId, FormDuty> {
  return patientPlansOfCareDuties.reduce(
    (acc, duty) => {
      return acc.set(duty.item.id, transformDuty(duty));
    },
    new Map<PlanOfCareItemId, FormDuty>()
  );
}

/**
 * Turns the duty-wide "Times per day" box and the per-day boxes under the
 * selected day circles into the two fields the API takes.
 *
 * The rule the API states: `timesPerDayByDay` is authoritative where present and
 * `timesPerDay` is the largest of its values. The rule this has to honour on top
 * of that: a plan nobody set a per-day count on must go out EXACTLY as it did
 * before the per-day boxes existed -- same `timesPerDay`, no map. So an untouched
 * duty short-circuits on `hasOverride` and never goes near the map, which matters
 * because filling one in would turn today's NULL `times_per_day` into a 1 on
 * every duty of every plan created from here.
 *
 * Only SELECTED days are read. A day can hold a leftover value from having been
 * selected, given a count, and then unselected again, and that value is not the
 * coordinator asking for anything.
 */
function resolveTimesPerDay(
  duty: FormDuty,
  itemLabel: string
):
  | { type: "Success"; timesPerDay: number | null; timesPerDayByDay: Record<string, number> | null }
  | { type: "Error"; message: string } {
  // An empty number input binds as null or undefined; anything else must be a
  // whole number of at least one occurrence.
  const base = duty.timesPerDay ?? null;

  if (base !== null && (!Number.isInteger(base) || base < 1)) {
    return {
      type: "Error",
      message: `Times per day must be a whole number of at least 1 for ${itemLabel}`,
    };
  }

  const byDay: Record<string, number> = {};
  let hasOverride = false;

  for (const day of duty.days) {
    const dayName = day.name();
    const override = duty.timesPerDayByDay[dayName] ?? null;

    if (override !== null && (!Number.isInteger(override) || override < 1)) {
      return {
        type: "Error",
        message: `Times per day on ${capitalizeDayName(dayName)} must be a whole number of at least 1 for ${itemLabel}`,
      };
    }

    if (override !== null) {
      hasOverride = true;
    }

    byDay[dayName] = override ?? base ?? 1;
  }

  if (!hasOverride) {
    return { type: "Success", timesPerDay: base, timesPerDayByDay: null };
  }

  const counts = Object.values(byDay);

  // Every selected day landed on the same number, so there is nothing per-day
  // left to say. Send it as a plain duty-wide count rather than a map that
  // repeats one value -- including when the coordinator typed that number into
  // the day boxes instead of the duty-wide one, which is why this returns the
  // agreed count and not `base`.
  if (counts.every((count) => count === counts[0])) {
    return { type: "Success", timesPerDay: counts[0], timesPerDayByDay: null };
  }

  return { type: "Success", timesPerDay: Math.max(...counts), timesPerDayByDay: byDay };
}

/** "MONDAY" -> "Monday", for error messages only. */
function capitalizeDayName(dayName: string): string {
  return dayName.charAt(0) + dayName.slice(1).toLowerCase();
}

function getInitialItem(): FormDuty {
  return {
    days: new Set(),
    period: "EVERY_VISIT",
    frequency: { id: null },
    visitsPerWeek: { id: null },
    notes: null,
    timesPerDay: null,
    timesPerDayByDay: {},
    caregiverRestricted: false,
  };
}

function transformDuty(duty: PatientPlansOfCareDuty): FormDuty {
  return {
    days: new Set(duty.daysOfWeek),
    period: duty.period,
    frequency: { id: duty.frequency },
    visitsPerWeek: { id: duty.visitsPerWeek },
    notes: duty.notes,
    timesPerDay: duty.timesPerDay ?? null,
    // Carried across rather than dropped. A plan duplicated from a CO service
    // plan import used to lose its per-day counts here and come back as a flat
    // `timesPerDay` on every day.
    timesPerDayByDay: { ...(duty.timesPerDayByDay ?? {}) },
    caregiverRestricted: duty.caregiverRestricted === true,
  };
}
